import RequestFormLayout from 'components/layout/RequestFormLayout';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import useAccessFlow from 'core/accessRequestForm/useAccessFlow';
import ManuallyAddVisitor from 'components/Form/ManuallyAddVisitor';
import { deleteAccessRequest, searchNsipVisitors } from 'core/accessRequestForm/accessRequestFormApi';
import NsipVisitorSearch from 'components/Form/NsipVisitorSearch';
import SearchVisitors from 'components/Form/SearchVisitors';
import { ClickListData } from 'components/Form/ListSearch/types';
import VisitorsAdded from 'components/Form/VisitorsAdded';
import type { AddVisitorValues } from 'components/Form/ManuallyAddVisitor/types';
import { isEmpty } from 'underscore';
import { unwrapResult } from '@reduxjs/toolkit';
import { NsipVisitorSearchResult, VisitRequestFormResponsePayload } from 'core/accessRequestForm/types';
import { updateVisitors, clearNsipSearchResults } from 'core/accessRequestForm/accessRequestFormSlice';
import { ACCESS_STEPPER_LABELS, ACCESS_STEPPER_LABELS_RETAIL } from 'core/utils/stepperLabels';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import type { AddedVisitor } from './types';

const AccessVisitorSelectionForm: FC = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const { id } = useParams<{ id: string }>();
    const flow = useAccessFlow();
    const dispatch = useDispatch<AppDispatch>();
    const { visitRequest, stepData, nsipSearchResults, isSearching } = useSelector((state: CombinedStates) => state.accessRequestForm);
    const [addedVisitors, setAddedVisitors] = useState<AddedVisitor[]>([]);
    const [showAddManually, setShowAddManually] = useState<boolean>(false);
    const amending = navState?.amending;

    // Retail general access uses the same saved/department visitor pool as Phase 1
    // and Guarding — no NSIP search, no NSIP review.
    const isRetail = stepData?.isRetail || false;

    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!flow.guestMode && !amending) ? visitRequest.encryptedId : null,
        formBasePath: '/access-request/',
        deleteEndpoint: 'access-request',
        dispatchDelete: () => {
            if (visitRequest.encryptedId) dispatch(deleteAccessRequest(visitRequest.encryptedId));
        },
    });

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(2, navigationState);
    };

    const handleInitialLoad = async () => {
        if (id !== undefined || flow.guestMode) {
            dispatch(clearNsipSearchResults());
            const resultAction = await flow.loadStep(3, amending);

            // Redirect to dashboard if access request not found
            if (resultAction.meta.requestStatus === 'rejected') {
                // A guest has no dashboard and no way to create a request: send
                // them back to the link itself, which re-validates the token and
                // shows the invalid/expired message when that is the reason.
                if (flow.guestMode) {
                    navigation(`/booking/${flow.token}`, { replace: true });
                    return;
                }

                navigation('/dashboard', { replace: true });
                notification.error({
                    message: 'Access Request Not Found',
                    description: 'The access request could not be found. Please create a new request.',
                });
            }
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Access Request',
                description: 'Please select an access request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    useEffect(() => {
        if (visitRequest.visitors) {
            setAddedVisitors(visitRequest.visitors);
        }
    }, [visitRequest.visitors]);

    const handleNextClick = (navigationState: Record<string, unknown>) => {
        let error = { message: '', description: '' };
        if (isEmpty(addedVisitors)) {
            error = {
                message: 'Visitor Not Selected',
                description: 'Please select a visitor to proceed',
            };
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }

        if (!isEmpty(addedVisitors) && (id !== undefined || flow.guestMode)) {
            flow.updateStep(3, {
                visitors: addedVisitors,
                amending,
                forcedStep: 3,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    // Retail skips the Access Type step (4) and goes straight to Job Details (1).
                    flow.goToStep(isRetail ? 1 : 4, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    // Retail: saved/department pool search (mirrors Phase 1 / Guarding).
    let visitorsSearchList: ClickListData[] = [];
    if (isRetail && stepData?.visitors !== undefined) {
        visitorsSearchList = stepData.visitors.map((visitor) => ({
            value: visitor.id,
            leftString: visitor.name,
            rightString: visitor.email,
        })).sort((a, b) => a.leftString.toLowerCase().localeCompare(b.leftString.toLowerCase()));
    }

    const handleSearchVisitorSelect = (value: string | number) => {
        // ClickListData.value is typed string | number; visitor.id is numeric, so
        // normalize before the strict comparison to avoid a string/number mismatch.
        const normalizedValue = typeof value === 'string' ? Number(value) : value;
        const foundVisitor = stepData?.visitors?.find((visitor) => visitor.id === normalizedValue);
        if (foundVisitor) {
            setAddedVisitors((prev) => {
                if (!prev.some((visitor) => visitor.visitorId === foundVisitor.id)) {
                    return [...prev, {
                        id: foundVisitor.id,
                        name: foundVisitor.name,
                        email: foundVisitor.email,
                        visitorId: foundVisitor.id,
                        companyName: foundVisitor.companyName || '',
                        phoneNumber: foundVisitor.phoneNumber || '',
                    }];
                }
                return prev;
            });
        }
    };

    const addedVisitorsIds: number[] = addedVisitors
        .filter((v) => v.visitorId)
        .map((v) => v.visitorId as number);

    const handleNsipSearch = (query: string) => {
        dispatch(searchNsipVisitors(query));
    };

    const handleNsipVisitorSelect = (nsipVisitor: NsipVisitorSearchResult) => {
        setAddedVisitors((prev) => {
            if (!prev.some(v => v.nsipVisitorId === nsipVisitor.id)) {
                const newVisitor: AddedVisitor = {
                    name: nsipVisitor.name,
                    email: nsipVisitor.email || '',
                    // The search already returns a redacted email; keep it as
                    // the masked value too so the display stays asterisked.
                    maskedEmail: nsipVisitor.email || '',
                    companyName: nsipVisitor.companyName || '',
                    phoneNumber: nsipVisitor.phoneNumber || '',
                    nsipVisitorId: nsipVisitor.id,
                    nsipReference: nsipVisitor.nsipReference || '',
                    // An expired NSIP code is not a valid NSIP — it must be
                    // approved by an approver, so flag it rather than marking
                    // it valid.
                    isExpired: nsipVisitor.isExpired || false,
                    isValidNsip: !nsipVisitor.isExpired,
                };
                return [...prev, newVisitor];
            }
            return prev;
        });
    };

    const handleRemoveVisitor = (visitor: AddedVisitor) => {
        setAddedVisitors((prev) => prev.filter((v) => {
            if (visitor.nsipVisitorId) {
                return v.nsipVisitorId !== visitor.nsipVisitorId || v.name !== visitor.name;
            }
            if (visitor.visitorId) {
                return v.visitorId !== visitor.visitorId;
            }
            return v.name !== visitor.name || v.email !== visitor.email;
        }));
        const newVisitors = addedVisitors.filter((v) => {
            if (visitor.nsipVisitorId) {
                return v.nsipVisitorId !== visitor.nsipVisitorId || v.name !== visitor.name;
            }
            if (visitor.visitorId) {
                return v.visitorId !== visitor.visitorId;
            }
            return v.name !== visitor.name || v.email !== visitor.email;
        });
        dispatch(updateVisitors(newVisitors));
    };

    const handleEnterManuallyClick = () => {
        setShowAddManually(true);
    };

    const handleAddVisitorManually = (values: AddVisitorValues) => {
        const newVisitor: AddedVisitor = {
            name: values.fullName,
            email: values.email,
            companyName: values.companyName,
            phoneNumber: values.phoneNumber,
            isTemporary: !values.saveVisitor,
        };
        setAddedVisitors((prev) => [...prev, newVisitor]);
        dispatch(updateVisitors([...addedVisitors, newVisitor]));
        setShowAddManually(false);
    };

    const selectedNsipIds: number[] = addedVisitors
        .filter((v) => v.nsipVisitorId)
        .map((v) => v.nsipVisitorId as number);

    return (
        <RequestFormLayout
            currentStep={isRetail ? 2 : 3}
            isNextEnabled
            isBackEnabled
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}
            stepperLabels={isRetail ? ACCESS_STEPPER_LABELS_RETAIL : ACCESS_STEPPER_LABELS}
            formTitle="Access Request Form"
            reduxSliceKey="accessRequestForm">
            <>
                {isRetail ? (
                    <SearchVisitors
                        savedVisitors={visitorsSearchList}
                        onSelect={handleSearchVisitorSelect}
                        onClickEnterManually={handleEnterManuallyClick}
                        selectedVisitors={addedVisitorsIds} />
                ) : (
                    <NsipVisitorSearch
                        onSearch={handleNsipSearch}
                        onSelect={handleNsipVisitorSelect}
                        onClickEnterManually={handleEnterManuallyClick}
                        searchResults={nsipSearchResults}
                        isSearching={isSearching}
                        selectedNsipIds={selectedNsipIds} />
                )}
                {showAddManually && <ManuallyAddVisitor addVisitorSubmit={handleAddVisitorManually} companyNameLabel="Company Name (Non-Vodafone)" saveVisitorLabel="Save visitor details to system to save entering their details again in the future" />}
                {addedVisitors.length > 0 && (
                    <VisitorsAdded
                        addedVisitors={addedVisitors}
                        removeVisitor={handleRemoveVisitor} />
                )}
            </>
        </RequestFormLayout>
    );
};

export default AccessVisitorSelectionForm;
